import { useState } from "react";
import { LocateFixed, MapPin, Search } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { storeErrorKey } from "@/lib/stores";

const countryName = (config, cc, lang) => config.country_names?.[cc]?.[lang] || cc;

// Étape 1 : position GPS (facultative) ou adresse / code postal → candidats → pays déduit (corrigeable)
export function StoreLocate({ config, onConfirm, onCancel }) {
  const { t, lang } = useLang();
  const [showAddress, setShowAddress] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [override, setOverride] = useState({});

  const locate = async (body) => {
    setBusy(true); setError(null); setCandidates(null);
    try {
      const { data } = await api.post("/stores/locate", body);
      if (!data.candidates.length) setError(t("st_no_place"));
      else setCandidates(data.candidates);
    } catch (e) {
      setError(e.response?.status === 400 ? t("st_no_place") : t(storeErrorKey(e)));
    } finally { setBusy(false); }
  };

  const useGps = () => {
    if (!navigator.geolocation) { setError(t("st_geo_denied")); setShowAddress(true); return; }
    setBusy(true); setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => locate({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { setBusy(false); setError(t("st_geo_denied")); setShowAddress(true); },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const confirm = (c, i) => {
    const country = override[i] || c.country;
    if (!config.countries.includes(country)) return;
    onConfirm({ ...c, country, country_detected: c.country });
  };

  return (
    <div className="mt-5" data-testid="store-locate">
      <p className="text-sm text-muted-foreground" data-testid="store-locate-why">{t("st_intro_why")}</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <Button type="button" onClick={useGps} disabled={busy} className="rounded-full bg-primary hover:bg-chenia-terracottadark justify-start h-11" data-testid="store-use-location">
          <LocateFixed className="h-4 w-4 mr-2" />{busy && !showAddress ? t("st_locating") : t("st_use_location")}
        </Button>
        <Button type="button" variant="outline" onClick={() => setShowAddress(true)} className="rounded-full justify-start h-11" data-testid="store-enter-address">
          <MapPin className="h-4 w-4 mr-2" />{t("st_enter_address")}
        </Button>
      </div>

      {showAddress && (
        <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (query.trim()) locate({ query: query.trim() }); }} data-testid="store-address-form">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("st_address_placeholder")} maxLength={200} className="rounded-full h-11" data-testid="store-address-input" />
          <Button type="submit" disabled={busy || !query.trim()} className="rounded-full h-11 bg-primary hover:bg-chenia-terracottadark" data-testid="store-address-submit">
            <Search className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">{t("st_search")}</span>
          </Button>
        </form>
      )}

      {error && <p className="mt-3 text-sm text-chenia-terracottadark" data-testid="store-locate-error">{error}</p>}

      {candidates && (
        <div className="mt-4 space-y-2" data-testid="store-candidates">
          {candidates.length > 1 && <p className="text-sm font-medium" data-testid="store-ambiguous">{t("st_confirm_place")}</p>}
          {candidates.map((c, i) => {
            const chosen = override[i] || c.country;
            const supported = config.countries.includes(chosen);
            return (
              <div key={c.place_id || i} className="rounded-2xl border border-border bg-background/60 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`store-candidate-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{c.label || c.locality || `${c.lat.toFixed(3)}, ${c.lng.toFixed(3)}`}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("st_country")} : <span data-testid={`store-candidate-country-${i}`}>{countryName(config, c.country, lang) || "—"}</span> ({t("st_country_detected")}) · {t(`st_precision_${c.precision === "approximate" ? "area" : c.precision}`)}
                  </p>
                  {!config.countries.includes(c.country) && !override[i] && (
                    <p className="text-xs text-chenia-terracottadark mt-1" data-testid={`store-candidate-unsupported-${i}`}>{t("st_country_unsupported", { country: countryName(config, c.country, lang) || "?" })}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={chosen || ""} onChange={(e) => setOverride((o) => ({ ...o, [i]: e.target.value }))} aria-label={t("st_fix_country")} data-testid={`store-candidate-country-select-${i}`}
                    className="h-9 rounded-full border border-border bg-card px-3 text-xs">
                    {!config.countries.includes(c.country) && <option value={c.country || ""}>{countryName(config, c.country, lang) || "—"}</option>}
                    {config.countries.map((cc) => <option key={cc} value={cc}>{countryName(config, cc, lang)}</option>)}
                  </select>
                  <Button type="button" size="sm" disabled={!supported} onClick={() => confirm(c, i)} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid={`store-candidate-confirm-${i}`}>{t("continue")}</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {onCancel && <button type="button" onClick={onCancel} className="mt-3 text-xs text-muted-foreground underline" data-testid="store-locate-cancel">{t("cancel")}</button>}
    </div>
  );
}
